export function parseUTCTimestamp(date: Date | string): Date {
  if (date instanceof Date) return date
  if (typeof date === 'string') {
    // Date-only "YYYY-MM-DD" must be parsed as local midnight: `new Date`
    // treats it as UTC per ECMA-262, which renders as the previous day in
    // negative-offset timezones — the date-picker off-by-one bug.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
    if (dateOnly) {
      const [, y, m, d] = dateOnly
      return new Date(Number(y), Number(m) - 1, Number(d))
    }
    if (
      date.includes('T') &&
      !date.endsWith('Z') &&
      !/[+-]\d{2}:\d{2}$/.test(date)
    ) {
      return new Date(date + 'Z')
    }
  }
  return new Date(date)
}

export function formatDate(date: Date | string): string {
  const d = parseUTCTimestamp(date)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d)
}

export function formatDateShort(date: Date | string): string {
  const d = parseUTCTimestamp(date)
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatDateTime(
  date: Date | string | null | undefined
): string | null {
  if (!date) return null
  const d = parseUTCTimestamp(date)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function tryParseJson(value: string | null): object | null {
  if (!value) return null
  try {
    return JSON.parse(value) as object
  } catch {
    return null
  }
}

export function formatRelativeTime(date: Date | string): string {
  const now = new Date()
  const d = parseUTCTimestamp(date)
  const diffMs = now.getTime() - d.getTime()
  const diffMinutes = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMinutes < 1) return 'Just now'
  if (diffMinutes < 60) return `${diffMinutes}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return formatDateShort(d)
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount)
}

export function truncate(str: string, length: number): string {
  return str.length > length ? str.substring(0, length) + '...' : str
}

/**
 * Builds formatted hierarchy breadcrumb: "Division / Department / Class / Subclass"
 * Example: "Arts & Crafts / Painting Supplies / Acrylic Paints / Artist Grade Acrylics"
 */
export function buildHierarchyBreadcrumb(
  hierarchy: { lineage: { name: string }[]; name: string } | null
): string {
  if (!hierarchy) return '-'

  // Build path from lineage + current level
  const lineagePath = hierarchy.lineage.map(item => item.name)
  const fullPath = [...lineagePath, hierarchy.name]

  return fullPath.join(' / ')
}

interface UserLike {
  id: string
  display_name?: string | null
  email?: string | null
}

/**
 * Formats a user's display name with fallback chain:
 * display_name → email → "User {id}"
 */
export function formatUserDisplayName(
  user: UserLike | null | undefined
): string {
  if (!user) return '-'
  if (user.display_name) return user.display_name
  if (user.email) return user.email
  return `User ${user.id}`
}
